// ─────────────────────────────────────────────────────────────────────────────
// AgentTabBar — DASHBOARD | CHAT top tab switch for the AgentView (Chunk 6).
//
// Desktop port of mobile/rexy-ios/src/components/shared/AgentTabBar.tsx. The
// active tab carries the domain accent in a 2px bottom border. Transparent root
// so the AppShell atmosphere bleeds through. Switching tabs is local UI state
// in AgentViewScreen.
//
// ── THE RUN CONTROL LIVES HERE NOW (owner ruling 2026-08-31) ──────────────
// "THE HEADER BAR ALREADY CARRIES DASHBOARD AND CHAT ON THAT EXACT ROW. A THIRD
// CONTROL THERE IS THE NATURAL HOME AND IT COSTS NO VERTICAL SPACE." It came
// out of Mission Control's rail card, where it had taken Buck's row to four
// times the height of every other agent's.
//
// ── IT READS THE PAIR FROM CONTEXT, AND THAT IS NOT LAZINESS ──────────────
// `AgentViewScreen` could pass {domain, agent} down — but that file currently
// holds SOMEBODY ELSE'S UNCOMMITTED WORK, and a pathspec commit takes whole
// files, not hunks. Touching it would sweep the Arthur/TradingPage workstream
// into this commit. AppShellLiveContext already carries navDomain/navAgent —
// it is the same mechanism that let the composer controls reach the pair
// without ChatSurface.tsx being touched — so the file is left alone.
//
// The tabs stay flex:1 and the control sits OUTSIDE them, so DASHBOARD and CHAT
// keep the width they had and the bar keeps its height.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useContext } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { AppShellLiveContext } from './AppShellContext';
import AgentRunControl from '../agents/AgentRunControl';

export type AgentTab = 'dashboard' | 'chat';

export interface AgentTabBarProps {
  active: AgentTab;
  accentColor: string; // domain accent from lib/agents.ts
  onChange: (tab: AgentTab) => void;
}

const TABS: { id: AgentTab; label: string }[] = [
  { id: 'dashboard', label: 'DASHBOARD' },
  { id: 'chat', label: 'CHAT' },
];

export default function AgentTabBar({ active, accentColor, onChange }: AgentTabBarProps) {
  const live = useContext(AppShellLiveContext);
  const domain = live?.navDomain || '';
  const agent = live?.navAgent || null;
  return (
    <div style={barStyle} role="tablist">
      {TABS.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            style={{
              ...tabStyle,
              borderBottomColor: isActive ? accentColor : 'transparent',
              color: isActive ? '#ffffff' : 'rgba(255,255,255,0.5)',
            }}
          >
            {t.label}
          </button>
        );
      })}
      {/* ONE AGENT, ON PURPOSE — the owner is looking at this agent's page, so
          this mount MAY read. `strip` is off: the header bar is one row and the
          live line belongs where there is room for it. */}
      {domain && agent && (
        <div style={controlSlotStyle}>
          <AgentRunControl
            domain={domain}
            agent={agent}
            strip={false}
            testId="header-run-control"
          />
        </div>
      )}
    </div>
  );
}

const barStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
  borderBottom: `1px solid ${REXY_CONSOLE.divider}`,
  padding: '0 16px',
};

// NO flex, so the bar's height stays whatever the tabs make it — the control is
// a 9px pill and must never be the thing that sets the row's height.
const controlSlotStyle: React.CSSProperties = {
  flex: '0 0 auto',
  paddingLeft: 12,
};

const tabStyle: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  padding: '14px 0',
  fontFamily: REXY_FONT,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.5,
  cursor: 'pointer',
  transition: 'color 200ms ease, border-color 200ms ease',
};
